/**
 * AtlasDebugMenuModule — a dev-gated, extensible admin debug panel.
 *
 * A floating DOM overlay (toggle: backtick `) that hosts debug tools as
 * registered sections. Ships with Weather + Day/Night controls, each with a
 * Local (your-view-only) / Shared (the whole Commons, via an existing reducer)
 * switch. Future debug tools call registerSection(title, build) to add a panel:
 *   app.getModule<AtlasDebugMenuModule>('DebugMenu')?.registerSection('Foo', (body) => { ... });
 *
 * Dev-gated: the panel + key listener exist ONLY on a local/dev origin or when
 * localStorage 'atlas:debug' === '1' — absent in production. Client-only +
 * purely additive: Shared mode reuses setWeather / debugSetSkyTime (no server
 * changes). Mirrors the AtlasPerfHudModule overlay pattern + the SkyModule gate.
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { adoptWindow, type UiWindow } from '../lib/ui_window.js';
import { nameFromTables } from '../lib/versona_names.js';
import { bus } from '../bus.js';
import { WeatherModule } from './WeatherModule.js';
import { setDebugTod } from './SkyModule.js';
import { settlementSites } from '@shared/settlements.js';
import { SCHEMATICS } from '../lib/schematic_catalog.js';
import { resolvedCityName } from '../lib/city_names.js';
import type { CommonsSettlementsModule } from './CommonsSettlementsModule.js';
import { findFeature, featureLine, FEATURE_KINDS, type FeatureKind, type FeatureMode, type FeatureSpot } from '../lib/terrain_features.js';
import { loadedRivers } from '../lib/hydrology_load.js';
import { bakedLakesNear } from '../lib/lakes.js';
import { commonsSeaLevel } from '../lib/sea_level.js';

// The canonical weather set (matches server WEATHERS + client OVERRIDE_INTENSITY).
const WEATHERS = ['clear', 'cloudy', 'rain', 'storm', 'fog', 'snow', 'sandstorm'] as const;
// Day/Night presets: label → time-of-day (0=midnight, 0.5=noon).
const TOD_PRESETS: ReadonlyArray<readonly [string, number]> = [
  ['Dawn', 0.25], ['Noon', 0.5], ['Dusk', 0.75], ['Midnight', 0.0],
];

export class AtlasDebugMenuModule implements IModule {
  readonly name = 'DebugMenu';
  private app!: App;
  private panel: HTMLDivElement | null = null;
  private content: HTMLDivElement | null = null;
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;
  private consoleInput: HTMLInputElement | null = null;
  /** The SWG-style window chrome around the panel (2026-09-12: wider + draggable). */
  private win: UiWindow | null = null;
  private readonly timers: number[] = [];

  init(app: App): void {
    this.app = app;
    if (!devGated()) {
      console.info(
        '[AtlasDebug] Debug menu disabled (non-dev origin). ' +
          "To enable: localStorage.setItem('atlas:debug','1') and reload.",
      );
      return; // inert in production — no panel, no listener
    }

    // ── Console commands (admin/dev): versona lookup + teleport ─────────────
    // __versonas('farmer')  → ranked table of all replicated versonas
    // __tpVersona(1234)     → teleport to that versona; no arg = random pick
    (window as unknown as Record<string, unknown>).__versonas = (filter?: string): unknown => {
      const conn = atlas.conn;
      if (!conn) return 'not connected';
      const f = (filter ?? '').toLowerCase();
      const rows: Array<{ id: string; archetype: string; state: string; x: number; z: number; dist: number }> = [];
      for (const v of conn.db.versona.iter()) {
        if (f && !v.archetype.toLowerCase().includes(f)) continue;
        // Hot position when the row is in AOI range; cold anchor otherwise.
        const hot = (conn.db as unknown as { versonaState?: { versonaId: { find(id: bigint): { curX: number; curZ: number; state: string } | undefined } } }).versonaState?.versonaId.find(v.versonaId);
        const x = hot?.curX ?? v.curX;
        const z = hot?.curZ ?? v.curZ;
        rows.push({
          id: String(v.versonaId), archetype: v.archetype, state: hot?.state ?? v.state,
          x: Math.round(x), z: Math.round(z),
          dist: Math.round(Math.hypot(x - atlas.localX, z - atlas.localZ)),
        });
      }
      rows.sort((a, b) => a.dist - b.dist);
      console.table(rows.slice(0, 40));
      console.log(`${rows.length} versonas${f ? ` matching "${f}"` : ''} (nearest 40 shown) — __tpVersona(id) to visit one`);
      return rows;
    };
    (window as unknown as Record<string, unknown>).__tpVersona = (id?: number | string | bigint): string => {
      const conn = atlas.conn;
      if (!conn) return 'not connected';
      void conn.reducers.debugTeleportToVersona({ versonaId: BigInt(id ?? 0) })
        .catch((e: Error) => console.warn('[__tpVersona]', e.message));
      return id ? `teleporting to versona #${id}…` : 'teleporting to a random versona…';
    };

    this.buildPanel();

    // Backtick toggles the panel. Guard typing + auto-repeat (atlas-client style).
    this.keyHandler = (e: KeyboardEvent): void => {
      if (e.repeat) return;
      const tag = (document.activeElement as HTMLElement | null)?.tagName ?? '';
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === '`') { e.preventDefault(); this.toggle(); }
    };
    window.addEventListener('keydown', this.keyHandler);

    // Built-in sections (registered through the same public API future tools use).
    this.buildConsoleSection();
    this.buildSettlementsSection();
    this.buildTerrainSection();
    this.buildWeatherSection();
    this.buildDayNightSection();
    this.buildAdminSection();
    this.buildBountySection();
  }

  /**
   * Admin tools: a SOV purse faucet + a straight-to-space launch. Both back
   * onto ADMIN-GATED reducers (debugAdminGrantSov / debugEnterSpace) — the
   * server refuses non-admins regardless of what this dev panel shows.
   */
  private buildAdminSection(): void {
    this.registerSection('Admin', (body) => {
      const row = (label: string): HTMLDivElement => {
        const r = document.createElement('div');
        r.style.cssText = 'display:flex;gap:6px;align-items:center;margin-bottom:6px';
        const l = document.createElement('span');
        l.textContent = label;
        l.style.cssText = 'min-width:86px;color:#aab';
        r.appendChild(l);
        return r;
      };
      const note = document.createElement('div');
      note.style.cssText = 'font-size:11px;color:#778;margin-top:2px';

      // ── Grant SOV (travel purse: shuttle 10, residency 50, group 1000) ────
      const moneyRow = row('Grant SOV');
      const amt = document.createElement('input');
      amt.type = 'number';
      amt.value = '1000';
      amt.min = '1';
      amt.max = '100000';
      amt.style.cssText = 'width:80px;background:#141821;color:#dde;border:1px solid #2a2f3a;border-radius:4px;padding:2px 6px';
      const grantBtn = document.createElement('button');
      grantBtn.textContent = 'Add to my wallet';
      grantBtn.style.cssText = 'cursor:pointer;background:#1d2635;color:#9cf;border:1px solid #2a3f5a;border-radius:4px;padding:2px 10px';
      grantBtn.onclick = (): void => {
        const conn = atlas.conn;
        if (!conn) { note.textContent = 'not connected'; return; }
        const n = Math.max(1, Math.min(100_000, Math.round(Number(amt.value) || 0)));
        void conn.reducers.debugAdminGrantSov({ amountSov: BigInt(n) })
          .then(() => { note.textContent = `+${n} SOV granted (ledger: PLATFORM → you)`; })
          .catch((e: Error) => { note.textContent = `refused: ${e.message}`; });
      };
      moneyRow.append(amt, grantBtn);

      // ── Launch to space (admin door — no spaceport needed) ────────────────
      const spaceRow = row('Space');
      const launchBtn = document.createElement('button');
      launchBtn.textContent = 'Launch to space sector';
      launchBtn.style.cssText = grantBtn.style.cssText;
      launchBtn.onclick = async (): Promise<void> => {
        const conn = atlas.conn;
        if (!conn) { note.textContent = 'not connected'; return; }
        try {
          await conn.reducers.debugEnterSpace({});
        } catch (e) {
          note.textContent = `refused: ${(e as Error).message}`;
          return;
        }
        // The server moved our playerState into the space world — ADOPT it
        // (mirror of AtlasTravelModule.launch()'s adoption loop).
        let spaceId: bigint | null = null;
        for (let i = 0; i < 40 && spaceId === null; i++) {
          for (const w of conn.db.world.iter()) if (w.kind === 'space') { spaceId = w.worldId; break; }
          if (spaceId === null) await new Promise((r) => setTimeout(r, 250));
        }
        if (spaceId === null) { note.textContent = 'the sector did not answer — try again'; return; }
        const me = conn.identity ? conn.db.playerState.identity.find(conn.identity) : null;
        bus.emit('atlas:world-adopted', { worldId: spaceId, x: me?.posX ?? 0, z: me?.posZ ?? 0 });
        note.textContent = 'launched — J to fly, exit returns you to the origin city spaceport';
      };
      spaceRow.append(launchBtn);

      // ── Grant EVERY craftable item (components + finished goods) ──────────
      // Serial debugGrantItem over the whole schematic catalog — admin-gated
      // server-side; ~1 call per item, progress in the note line.
      const itemsRow = row('Items');
      const allBtn = document.createElement('button');
      allBtn.textContent = 'Grant ALL craftable items';
      allBtn.style.cssText = grantBtn.style.cssText;
      allBtn.onclick = async (): Promise<void> => {
        const conn = atlas.conn;
        if (!conn) { note.textContent = 'not connected'; return; }
        allBtn.disabled = true;
        let done = 0, refused = 0;
        for (const s of SCHEMATICS) {
          try {
            await conn.reducers.debugGrantItem({ itemKey: s.key, quality: 900 });
            done++;
          } catch { refused++; }
          note.textContent = `granting… ${done + refused}/${SCHEMATICS.length}`;
        }
        allBtn.disabled = false;
        note.textContent = refused === 0
          ? `granted all ${done} craftables at q900 — F6 → Inventory (rig deeds land in your holdings)`
          : `granted ${done}/${SCHEMATICS.length} (${refused} refused — admin required?)`;
      };
      const clearBtn = document.createElement('button');
      clearBtn.textContent = 'Remove ALL items';
      clearBtn.style.cssText = grantBtn.style.cssText.replace('#9cf', '#f9a').replace('#2a3f5a', '#5a2a3f');
      clearBtn.onclick = (): void => {
        const conn = atlas.conn;
        if (!conn) { note.textContent = 'not connected'; return; }
        let n = 0, nd = 0;
        try {
          for (const it of conn.db.playerItem.iter()) if (conn.identity && it.owner.isEqual(conn.identity)) n++;
          for (const d of conn.db.structureDeed.iter()) if (conn.identity && d.owner.isEqual(conn.identity) && d.packed) nd++;
        } catch { /* row cache mid-churn */ }
        void conn.reducers.debugClearInventory({})
          .then(() => { note.textContent = `swept ${n} items + ${nd} packed deeds (placed structures/equipment/resources kept)`; })
          .catch((e: Error) => { note.textContent = `refused: ${e.message}`; });
      };
      itemsRow.append(allBtn, clearBtn);

      body.append(moneyRow, spaceRow, itemsRow, note);
    });
  }

  /** Append a titled section; the caller fills `body` with its own controls. */
  registerSection(title: string, build: (body: HTMLElement) => void): void {
    if (!this.content) return;
    const section = document.createElement('div');
    section.style.cssText = 'margin:0 0 10px;border-top:1px solid #2a2f3a;padding-top:8px';
    const h = document.createElement('div');
    h.textContent = title;
    h.style.cssText = 'font-weight:600;margin-bottom:6px;color:#9cf';
    const body = document.createElement('div');
    section.append(h, body);
    this.content.appendChild(section);
    build(body);
  }

  private toggle(): void {
    if (!this.panel) return;
    const show = this.panel.style.display === 'none';
    this.panel.style.display = show ? 'block' : 'none';
    if (show) this.consoleInput?.focus(); // open → ready to type a command
  }

  private buildPanel(): void {
    const panel = document.createElement('div');
    panel.id = 'debug-panel';
    // 2026-09-12 ("make this window wider and draggable"): the panel lives in
    // the shared SWG-style window chrome (lib/ui_window adoptWindow — drag,
    // resize, roll-up, opacity, remembered position) at 2× its old width; the
    // module keeps toggling `style.display` and the chrome mirrors it.
    panel.style.cssText =
      'display:none;overflow-y:auto;height:100%;box-sizing:border-box;background:rgba(12,14,20,0.92);color:#dde;' +
      'font:12px/1.45 system-ui,sans-serif;padding:10px 12px';
    const title = document.createElement('div');
    title.style.cssText = 'font-size:11px;margin-bottom:8px;color:#889;display:flex;gap:8px;align-items:center';
    const titleText = document.createElement('span');
    titleText.textContent = '` toggles this window · drag the title bar to move it';
    titleText.style.flex = '1';
    // 2026-09-18: the PROBES window — the perf tour + every diagnostic probe, run and recorded by the admin
    // ("add a probe button and have a separate window that pops up for different probes").
    const probes = document.createElement('button');
    probes.id = 'debug-open-probes';
    probes.textContent = 'Probes…';
    probes.title = 'the Probes window: the perf tour, frame timing, censuses, every diagnostic lever — run and recorded';
    probes.style.cssText = 'background:#243046;color:#dde;border:1px solid #3a4a66;border-radius:4px;padding:1px 8px;cursor:pointer;font:11px system-ui';
    probes.onclick = (): void => {
      const p = (window as unknown as { __probes?: { open(): boolean } }).__probes;
      if (!p) { titleText.textContent = 'Probes module not loaded'; return; }
      if (!p.open()) titleText.textContent = 'Probes need the Commons admin capability';
    };
    title.append(titleText, probes);
    const content = document.createElement('div');
    panel.append(title, content);
    const W = 560;
    this.win = adoptWindow(panel, {
      id: 'cosmos-debug', title: 'Cosmos Debug', icon: 'gear',
      x: Math.max(12, (typeof window !== 'undefined' ? window.innerWidth : 1280) - W - 12), y: 12,
      width: W, height: 700, minWidth: 360, minHeight: 240,
    });
    this.panel = panel;
    this.content = content;
  }

  // ── Built-in section: Bounties — who hired a hunter on whom (2026-09-12) ──
  //    Every contract on the shard (the table is public; the dev panel
  //    subscribes to ALL rows the first time the section refreshes — the game
  //    itself only ever subscribes to `target = :sender` / `placed_by = :sender`).
  //    "On you" first: the contracts whose target is the admin player.
  private buildBountySection(): void {
    this.registerSection('Bounties — who hired a hunter on whom', (body) => {
      const note = document.createElement('div');
      note.style.cssText = 'font-size:11px;color:#778;margin-bottom:6px';
      note.textContent = 'Contractor → target · hunter · tier · state · placed. Refreshes every 2 s while open.';
      const mine = document.createElement('div');
      mine.style.cssText = 'margin:0 0 8px;padding:6px 8px;border:1px solid #5a3a2a;border-radius:6px;background:rgba(60,24,18,0.35);font:11px/1.5 system-ui,sans-serif';
      const wrap = document.createElement('div');
      wrap.style.cssText = 'max-height:280px;overflow:auto;font:11px/1.4 ui-monospace,monospace;white-space:nowrap';
      body.append(note, mine, wrap);
      let subbed = false;
      const ensureSub = (): void => {
        if (subbed || !atlas.conn) return;
        subbed = true;
        try { atlas.conn.subscriptionBuilder().subscribe(['SELECT * FROM bounty_contract', 'SELECT * FROM bounty_hunt', 'SELECT * FROM bounty_debug']); } catch { /* pre-publish bindings */ }
      };
      const refresh = (): void => {
        const conn = atlas.conn;
        if (!conn) { mine.textContent = 'not connected'; return; }
        const rows = [...this.tbl<BountyRow>('bountyContract')];
        const me = conn.identity;
        const nowUs = BigInt(Date.now()) * 1000n;
        const age = (t: { microsSinceUnixEpoch: bigint }): string => {
          const s = Number((nowUs - t.microsSinceUnixEpoch) / 1_000_000n);
          return s < 90 ? `${Math.max(0, s)}s` : s < 5400 ? `${Math.round(s / 60)}m` : s < 172800 ? `${Math.round(s / 3600)}h` : `${Math.round(s / 86400)}d`;
        };
        const player = (id: { toHexString(): string; isEqual(o: unknown): boolean }): string => {
          if (me && id.isEqual(me)) return 'YOU';
          const acct = (conn.db as unknown as { account?: { identity: { find(i: unknown): { username: string } | undefined } } }).account?.identity.find(id);
          return acct?.username ?? id.toHexString().slice(0, 8) + '…';
        };
        const contractor = (b: BountyRow): string => {
          if (b.contractorKind === 'player') return `player ${player(b.placedBy)}`;
          if (b.contractorKind === 'versona') { const n = nameFromTables(conn, b.contractorId); return `versona ${n && !/^#|^versona/.test(n) ? n : b.contractorLabel} #${b.contractorId}`; }
          return `${b.contractorKind} ${b.contractorLabel}`;
        };
        const active = (b: BountyRow): boolean => b.state === 'stalking' || b.state === 'attacking';
        rows.sort((a, b) => Number(active(b)) - Number(active(a)) || Number(b.placedAt.microsSinceUnixEpoch - a.placedAt.microsSinceUnixEpoch));
        // On you.
        const onMe = me ? rows.filter((b) => b.target.isEqual(me)) : [];
        if (!onMe.length) mine.textContent = 'No contract has ever been placed on you.';
        else {
          mine.replaceChildren();
          const h = document.createElement('div'); h.style.cssText = 'font-weight:600;color:#f9a;margin-bottom:2px';
          h.textContent = `On YOU: ${onMe.filter(active).length} active of ${onMe.length}`;
          mine.appendChild(h);
          const hunts = new Map<string, { phase: string; materialized: boolean; x: number; z: number; revision: number }>();
          for (const h of this.tbl<{ bountyId: bigint; phase: string; materialized: boolean; x: number; z: number; revision: number }>('bountyHunt')) hunts.set(h.bountyId.toString(), h);
          const debugRows = new Map<string, string>();
          for (const d of this.tbl<{ bountyId: bigint; json: string }>('bountyDebug')) debugRows.set(d.bountyId.toString(), d.json);
          for (const b of onMe.slice(0, 8)) {
            const l = document.createElement('div');
            const h = hunts.get(b.bountyId.toString());
            // The stalking rework: the encounter PHASE beside the contract state (the body is where the server says).
            const enc = h ? ` · ${h.phase}${h.materialized ? ` @ ${h.x.toFixed(0)},${h.z.toFixed(0)} (${Math.hypot(h.x - atlas.localX, h.z - atlas.localZ).toFixed(0)} m)` : ' (no body)'} r${h.revision}` : '';
            l.textContent = `${active(b) ? '●' : '○'} ${contractor(b)} hired ${b.hunterName} (tier ${b.tier}, ${b.spend} ⓒ) — ${b.state}${enc}, ${age(b.placedAt)} ago${active(b) ? `, hunter ${b.hunterHp}/${b.hunterMaxHp} hp` : ''}`;
            mine.appendChild(l);
            if (active(b)) {
              // Debug-gated: a JSON snapshot of the PRIVATE agent + pressure rows (belief, opening, pacing, the commit gate's reason).
              const row = document.createElement('div'); row.style.cssText = 'margin:2px 0 4px 12px;font:10px ui-monospace,monospace;color:#9ab;white-space:pre-wrap;word-break:break-all';
              const btn = document.createElement('button'); btn.textContent = 'inspect hunt'; btn.style.cssText = 'font:10px ui-monospace,monospace;margin-right:6px';
              btn.onclick = () => { (conn.reducers as unknown as { debugBountyInspect(a: { bountyId: bigint }): Promise<void> }).debugBountyInspect({ bountyId: b.bountyId }).catch((e: Error) => { row.textContent = String(e.message ?? e); }); };
              const txt = document.createElement('span'); txt.textContent = debugRows.get(b.bountyId.toString()) ?? '';
              row.append(btn, txt);
              mine.appendChild(row);
            }
          }
        }
        // Everyone.
        const table = document.createElement('table');
        table.style.cssText = 'border-collapse:collapse;width:100%';
        const head = document.createElement('tr');
        for (const t of ['', 'contractor', 'target', 'hunter', 'tier', 'state', 'placed']) { const th = document.createElement('th'); th.textContent = t; th.style.cssText = 'text-align:left;color:#9cf;padding:1px 6px 1px 0;position:sticky;top:0;background:#12141c'; head.appendChild(th); }
        table.appendChild(head);
        for (const b of rows.slice(0, 80)) {
          const tr = document.createElement('tr');
          const you = !!me && b.target.isEqual(me);
          if (you) tr.style.color = '#f9a';
          for (const c of [active(b) ? '●' : '○', contractor(b), player(b.target), b.hunterName, String(b.tier), b.state, age(b.placedAt)]) {
            const td = document.createElement('td'); td.textContent = c; td.style.cssText = 'padding:1px 6px 1px 0;border-top:1px solid #1e2430'; tr.appendChild(td);
          }
          table.appendChild(tr);
        }
        const foot = document.createElement('div'); foot.style.cssText = 'color:#778;margin-top:4px';
        foot.textContent = `${rows.length} contract${rows.length === 1 ? '' : 's'} on the shard · ${rows.filter(active).length} active${rows.length > 80 ? ' · newest 80 shown' : ''}`;
        wrap.replaceChildren(table, foot);
      };
      this.timers.push(window.setInterval(() => {
        if (!this.panel || this.panel.style.display === 'none') return;
        ensureSub();
        refresh();
      }, 2000));
    });
  }

  private tbl<T>(name: string): Iterable<T> {
    const t = (atlas.conn?.db as unknown as Record<string, { iter(): Iterable<T> } | undefined>)[name];
    return t ? t.iter() : [];
  }

  // ── Built-in section: Weather (Local → WeatherModule / Shared → setWeather) ──
  private buildWeatherSection(): void {
    this.registerSection('Weather', (body) => {
      let scope: 'local' | 'shared' = 'local';
      const scopeRow = makeScopeToggle((s) => { scope = s; });

      const apply = (weather: string | null): void => {
        if (scope === 'local') {
          this.app.getModule<WeatherModule>('Weather')?.setDebugWeather(weather);
        } else {
          const conn = atlas.conn;
          const worldId = atlas.currentWorldId;
          if (!conn || worldId == null) return;
          // 'Auto'/null in Shared mode = 'clear' (no live-ops override → biome×drift).
          try { conn.reducers.setWeather({ worldId, weather: weather ?? 'clear' }); }
          catch (e) { console.warn('[AtlasDebug] setWeather failed:', e); }
        }
      };

      const btns = document.createElement('div');
      btns.style.cssText = 'display:flex;flex-wrap:wrap;gap:4px;margin-top:6px';
      for (const w of WEATHERS) btns.appendChild(makeBtn(cap(w), () => apply(w)));
      btns.appendChild(makeBtn('Auto', () => apply(null)));

      // Live meteo readout (temperature/RH/dew point at the player) — the
      // field that PRODUCES the ambient weather; 1 Hz while the menu is open.
      const meteoRow = document.createElement('div');
      meteoRow.style.cssText = 'margin-top:6px;font:11px/1.5 monospace;opacity:0.85;white-space:pre';
      const renderMeteo = (): void => {
        const m = this.app.getModule<WeatherModule>('Weather')?.meteo;
        meteoRow.textContent = m
          ? `T ${m.temperatureC.toFixed(1)}°C  RH ${(m.humidity01 * 100).toFixed(0)}%  DP ${m.dewPointC.toFixed(1)}°C\n`
            + `precip ${(m.precip01 * 100).toFixed(0)}% (${m.kind})  cover ${(m.cover01 * 100).toFixed(0)}%  fog ${(m.fog01 * 100).toFixed(0)}%\n`
            + `wind ${(m.wind01 * 100).toFixed(0)}% → (${m.windDirX.toFixed(2)}, ${m.windDirZ.toFixed(2)})  instab ${(m.instability01 * 100).toFixed(0)}%`
          : '(meteo pending — enter the Commons)';
      };
      renderMeteo();
      window.setInterval(() => { if (body.offsetParent !== null) renderMeteo(); }, 1000); // panel closed → skip

      body.append(scopeRow, btns, meteoRow);
    });
  }

  // ── Built-in section: Day / Night (Local → setDebugTod / Shared → debugSetSkyTime) ──
  private buildDayNightSection(): void {
    this.registerSection('Day / Night', (body) => {
      let scope: 'local' | 'shared' = 'local';
      const scopeRow = makeScopeToggle((s) => { scope = s; });

      const setTod = (t: number | null): void => {
        if (scope === 'local') {
          setDebugTod(t);
        } else if (t !== null) { // no 'Auto' for Shared (the server clock just advances from t)
          const conn = atlas.conn;
          const worldId = atlas.currentWorldId;
          if (!conn || worldId == null) return;
          try { conn.reducers.debugSetSkyTime({ worldId, timeOfDay: t }); }
          catch (e) { console.warn('[AtlasDebug] debugSetSkyTime failed:', e); }
        }
      };

      const slider = document.createElement('input');
      slider.type = 'range'; slider.min = '0'; slider.max = '1'; slider.step = '0.01'; slider.value = '0.5';
      slider.style.cssText = 'width:100%;margin:6px 0';
      // Live local drag (cheap); one shared reducer call on release (no flooding).
      slider.addEventListener('input', () => { if (scope === 'local') setTod(parseFloat(slider.value)); });
      slider.addEventListener('change', () => { if (scope === 'shared') setTod(parseFloat(slider.value)); });

      const presets = document.createElement('div');
      presets.style.cssText = 'display:flex;flex-wrap:wrap;gap:4px';
      for (const [label, t] of TOD_PRESETS) {
        presets.appendChild(makeBtn(label, () => { slider.value = String(t); setTod(t); }));
      }
      presets.appendChild(makeBtn('Auto', () => setTod(null)));

      body.append(scopeRow, slider, presets);
    });
  }

  // ── Built-in section: command console (teleport + world queries; ` opens + focuses it) ──
  private buildConsoleSection(): void {
    this.registerSection('Console  ( type /help )', (body) => {
      const output = document.createElement('div');
      output.style.cssText =
        'max-height:128px;overflow-y:auto;background:#0a0c12;border:1px solid #2a2f3a;border-radius:4px;' +
        'padding:4px 6px;margin-bottom:6px;font:11px/1.45 monospace;color:#bcd;white-space:pre-wrap';
      const input = document.createElement('input');
      input.type = 'text';
      input.placeholder = '/tp 100 -50   ( Enter )';
      input.style.cssText =
        'width:100%;box-sizing:border-box;background:#11141c;color:#dfe;border:1px solid #456;border-radius:4px;padding:4px 6px;font:12px monospace';
      this.consoleInput = input;

      const out = (msg: string): void => {
        const line = document.createElement('div');
        line.textContent = msg;
        output.appendChild(line);
        output.scrollTop = output.scrollHeight;
      };
      const commonsSeed = (): string | null => {
        const conn = atlas.conn;
        if (!conn) return null;
        for (const w of conn.db.world.iter()) if (w.kind === 'commons') return w.terrainSeed;
        return null;
      };
      const teleport = (x: number, z: number): void => {
        const conn = atlas.conn;
        if (!conn) { out('not connected'); return; }
        try { conn.reducers.debugTeleport({ posX: x, posZ: z }); out(`→ tp (${x.toFixed(0)}, ${z.toFixed(0)})`); }
        catch (e) { out(`tp failed: ${String(e)}`); }
      };
      const run = (raw: string): void => {
        const line = raw.trim();
        if (!line) return;
        out(`> ${line}`);
        const parts = line.replace(/^\//, '').split(/\s+/);
        const cmd = (parts.shift() ?? '').toLowerCase();
        const arg = parts.join(' ');
        const seed = commonsSeed();
        const px = atlas.localX;
        const pz = atlas.localZ;
        const nearestFirst = <T extends { centerX: number; centerZ: number }>(a: T, b: T): number =>
          Math.hypot(a.centerX - px, a.centerZ - pz) - Math.hypot(b.centerX - px, b.centerZ - pz);
        switch (cmd) {
          case 'tp': {
            const n = arg.split(/[ ,]+/).map(Number).filter((v) => !Number.isNaN(v));
            if (n.length >= 2) teleport(n[0], n[1]); else out('usage: /tp <x> <z>');
            break;
          }
          case 'tp-home':
            teleport(0, 0);
            break;
          case 'credits': {
            // /credits <amount> [username] — admin faucet OR debit through
            // the zero-sum ledger (negative subtracts; the overdraft guard
            // refuses a debit past the wallet's balance).
            const conn = atlas.conn;
            if (!conn) { out('not connected'); break; }
            const amt = BigInt(Math.floor(Number(parts[0] ?? '0')) || 0);
            if (amt === 0n) { out('usage: /credits <amount|-amount> [username]'); break; }
            let target = conn.identity ?? null;
            const uname = parts[1];
            if (uname) {
              target = null;
              for (const a of conn.db.account.iter()) {
                if (a.username === uname) { target = a.identity; break; }
              }
              if (!target) { out(`no account '${uname}'`); break; }
            }
            if (!target) { out('no identity'); break; }
            void (conn.reducers as unknown as { debugGrantSov(a: { identity: unknown; amount: bigint }): Promise<void> })
              .debugGrantSov({ identity: target, amount: amt })
              .then(() => out(`${amt > 0n ? '+' : ''}${amt} ⓒ → ${uname ?? 'you'}`), (e: Error) => out(`refused: ${e.message ?? e}`));
            break;
          }
          case 'tp-biome': {
            if (!seed) { out('no Commons'); break; }
            const m = settlementSites(seed).filter((s) => s.biome === arg).sort(nearestFirst);
            if (!m.length) out(`no '${arg}' settlement — try /list-biomes`);
            else teleport(m[0].centerX, m[0].centerZ);
            break;
          }
          case 'tp-settlement': {
            if (!seed) { out('no Commons'); break; }
            const s = settlementSites(seed).find((x) => x.id === Number(arg));
            if (s) teleport(s.centerX, s.centerZ); else out(`no settlement #${arg} — try /list-settlements`);
            break;
          }
          case 'list-biomes': {
            if (!seed) { out('no Commons'); break; }
            const counts = new Map<string, number>();
            for (const s of settlementSites(seed)) counts.set(s.biome, (counts.get(s.biome) ?? 0) + 1);
            out('ground = regolith_plains everywhere (city pin); settlement themes:');
            for (const [b, n] of [...counts].sort()) out(`  ${b} ×${n}`);
            break;
          }
          case 'list-settlements': {
            if (!seed) { out('no Commons'); break; }
            for (const s of [...settlementSites(seed)].sort(nearestFirst)) {
              out(`#${s.id} ${s.tier} ${s.biome} (${s.centerX.toFixed(0)},${s.centerZ.toFixed(0)}) ${Math.hypot(s.centerX - px, s.centerZ - pz).toFixed(0)}m`);
            }
            break;
          }
          case 'list-cities': {
            if (!seed) { out('no Commons'); break; }
            const conn = atlas.conn;
            for (const s of [...settlementSites(seed)].sort(nearestFirst)) {
              out(`#${s.id} ${resolvedCityName(s, conn)} — ${s.tier} ${s.biome} (${s.centerX.toFixed(0)},${s.centerZ.toFixed(0)}) ${Math.hypot(s.centerX - px, s.centerZ - pz).toFixed(0)}m`);
            }
            break;
          }
          case 'tp-city': {
            if (!seed) { out('no Commons'); break; }
            const conn = atlas.conn;
            const sites = settlementSites(seed);
            const q = arg.toLowerCase();
            const s =
              sites.find((x) => resolvedCityName(x, conn).toLowerCase() === q) ??
              sites.find((x) => resolvedCityName(x, conn).toLowerCase().startsWith(q));
            if (s) teleport(s.centerX, s.centerZ); else out(`no city "${arg}" — try /list-cities`);
            break;
          }
          case 'name-city': {
            const conn = atlas.conn;
            if (!conn) { out('not connected'); break; }
            // arg = "<id> <name…>" e.g. "0 New Haven"
            const spaceIdx = arg.indexOf(' ');
            if (spaceIdx < 0) { out('usage: /name-city <id> <name>'); break; }
            const idStr = arg.slice(0, spaceIdx).trim();
            const name = arg.slice(spaceIdx + 1).trim();
            const siteId = parseInt(idStr, 10);
            if (!idStr || !Number.isInteger(siteId) || siteId < 0) { out('invalid site id — use the numeric id shown in /list-cities'); break; }
            if (!name) { out('name must not be empty'); break; }
            try {
              (conn.reducers as unknown as { setSettlementName(a: { siteId: bigint; name: string }): void })
                .setSettlementName({ siteId: BigInt(siteId), name });
              out(`site #${siteId} named '${name}' (pending server round-trip)`);
            } catch (e) { out(`name-city failed: ${String(e)}`); }
            break;
          }
          case 'fly':
            atlas.flyMode = !atlas.flyMode;
            out(atlas.flyMode ? 'fly mode ON — WASD · Space/Ctrl up-down · Shift boost · mouse look · F or /fly to exit' : 'fly mode OFF');
            break;
          case 'grant-admin': {
            const conn = atlas.conn;
            if (!conn) { out('not connected'); break; }
            try {
              (conn.reducers as unknown as { debugGrantCommonsAdmin(a: Record<string, never>): void }).debugGrantCommonsAdmin({});
              out('requested Commons admin — re-publish/reload to refresh caps');
            } catch (e) { out(`grant-admin failed: ${String(e)}`); }
            break;
          }
          case 'help':
            out('/tp <x> <z> · /tp-home · /tp-biome <biome> · /tp-settlement <id> · /tp-city <name> · /name-city <id> <name> · /list-biomes · /list-settlements · /list-cities · /fly · /grant-admin');
            break;
          default:
            out(`unknown: ${cmd} — try /help`);
        }
      };

      input.addEventListener('keydown', (e) => {
        e.stopPropagation(); // keep WASD / ` out of the game while typing a command
        if (e.key === 'Enter') { run(input.value); input.value = ''; }
        else if (e.key === 'Escape') { input.blur(); this.toggle(); }
      });
      body.append(output, input);
      out('Type /help for commands.');
    });
  }

  // ── Built-in section: Terrain features — ADMIN teleport to a cave, lake,
  //    river, waterfall, shore, sea, mountain, forest, plateau rim, terraces
  //    or any biome (2026-09-10). The spots come from the pure finders over
  //    the world's own oracles (lib/terrain_features.ts); the move is the
  //    debug-gated debugTeleport — the server refuses non-admins. Commons only.
  /** The pure lookup behind the teleport: a feature spot from the player's position, or the reason there is none. Commons only. */
  findFeatureSpot(kind: FeatureKind, mode: FeatureMode = 'nearest'): FeatureSpot | string {
    const conn = atlas.conn;
    if (!conn) return 'not connected';
    if (atlas.currentWorldId === null || atlas.currentWorldId !== atlas.commonsWorldId) return 'terrain features live on the Commons — Tab there first';
    const commons = [...conn.db.world.iter()].find((w) => w.kind === 'commons');
    if (!commons) return 'no commons world replicated';
    const seed = commons.terrainSeed;
    const found = findFeature(
      { seed, rivers: loadedRivers(seed), lakes: bakedLakesNear(seed, 0, 0, 4_000_000), sea: commonsSeaLevel(seed) },
      kind, atlas.localX, atlas.localZ, mode,
    );
    if (!found) return `no ${kind.replace(/_/g, ' ')} found${kind === 'river' || kind === 'waterfall' || kind === 'lake' ? ' (hydrology bake not loaded yet?)' : ''}`;
    return found;
  }

  /** The admin teleport to a feature spot (the debug-gated `debugTeleport`; the server refuses non-admins). */
  teleportToFeature(kind: FeatureKind, mode: FeatureMode = 'nearest'): FeatureSpot | string {
    const found = this.findFeatureSpot(kind, mode);
    if (typeof found === 'string') return found;
    void atlas.conn?.reducers.debugTeleport({ posX: found.x, posZ: found.z })
      .catch((e: Error) => console.warn('[__tpFeature]', e.message));
    return found;
  }

  private buildTerrainSection(): void {
    const tp = (kind: FeatureKind, mode: FeatureMode): FeatureSpot | string => this.teleportToFeature(kind, mode);
    (window as unknown as Record<string, unknown>).__tpFeature = (kind: FeatureKind, mode: FeatureMode = 'nearest'): FeatureSpot | string => tp(kind, mode);
    this.registerSection('Terrain features  (admin teleport)', (body) => {
      const note = document.createElement('div');
      note.style.cssText = 'font-size:11px;color:#9cf;margin-bottom:6px;min-height:14px';
      note.textContent = 'Nearest or a random one of each — Commons only, admins only (the server refuses otherwise).';
      body.appendChild(note);
      const grid = document.createElement('div');
      grid.style.cssText = 'display:grid;grid-template-columns:1fr auto auto;gap:3px 6px;align-items:center';
      for (const f of FEATURE_KINDS) {
        const l = document.createElement('span');
        l.textContent = f.label;
        l.title = f.hint;
        l.style.cssText = 'color:#aab;font-size:11px';
        const go = (mode: FeatureMode): void => {
          const r = tp(f.kind, mode);
          note.textContent = typeof r === 'string' ? r : `→ ${featureLine(r)}`;
          note.style.color = typeof r === 'string' ? '#f99' : '#9cf';
        };
        const near = makeBtn('Nearest', () => go('nearest'));
        near.dataset.feature = f.kind;
        near.dataset.mode = 'nearest';
        const rnd = makeBtn('Random', () => go('random'));
        rnd.dataset.feature = f.kind;
        rnd.dataset.mode = 'random';
        grid.append(l, near, rnd);
      }
      body.appendChild(grid);
    });
  }

  // ── Built-in section: Settlements (debug GLB-name labels above each building) ──
  private buildSettlementsSection(): void {
    this.registerSection('Settlements', (body) => {
      let on = false;
      const btn = makeBtn('Mesh names: OFF', () => {
        on = !on;
        btn.textContent = `Mesh names: ${on ? 'ON' : 'OFF'}`;
        btn.style.borderColor = on ? '#9cf' : '#456';
        this.app.getModule<CommonsSettlementsModule>('CommonsSettlements')?.setMeshLabels(on);
      });
      body.appendChild(btn);
    });
  }

  dispose(): void {
    if (this.keyHandler) { window.removeEventListener('keydown', this.keyHandler); this.keyHandler = null; }
    for (const t of this.timers) window.clearInterval(t);
    this.timers.length = 0;
    this.win?.root.remove();
    this.win = null;
    this.panel?.remove();
    this.panel = null;
    this.content = null;
    this.consoleInput = null;
  }
}

// ─── Module-scope DOM helpers ─────────────────────────────────────────────────

/** True on a local/dev origin or when localStorage 'atlas:debug'==='1'. */
/** A bounty_contract row as the panel reads it (the generated binding's shape). */
interface BountyRow {
  bountyId: bigint;
  target: { toHexString(): string; isEqual(o: unknown): boolean };
  contractorKind: string;
  contractorId: bigint;
  contractorLabel: string;
  placedBy: { toHexString(): string; isEqual(o: unknown): boolean };
  spend: bigint;
  tier: number;
  hunterName: string;
  hunterHp: number;
  hunterMaxHp: number;
  state: string;
  placedAt: { microsSinceUnixEpoch: bigint };
}

function devGated(): boolean {
  const host = (typeof location !== 'undefined' ? location.hostname : '') || '';
  const local = host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host === '';
  let optIn = false;
  try { optIn = typeof localStorage !== 'undefined' && localStorage.getItem('atlas:debug') === '1'; }
  catch { /* storage blocked (private-browsing / permissions policy) */ }
  return local || optIn;
}

function makeBtn(label: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.textContent = label;
  b.style.cssText =
    'background:#223;color:#cde;border:1px solid #456;border-radius:4px;' +
    'padding:3px 7px;cursor:pointer;font:11px system-ui,sans-serif';
  b.addEventListener('click', onClick);
  return b;
}

/** A Local/Shared segmented toggle; fires onChange + highlights the active half. Defaults to Local. */
function makeScopeToggle(onChange: (s: 'local' | 'shared') => void): HTMLElement {
  const row = document.createElement('div');
  row.style.cssText = 'display:flex;gap:4px';
  const local = makeBtn('Local', () => set('local'));
  const shared = makeBtn('Shared', () => set('shared'));
  function set(s: 'local' | 'shared'): void {
    onChange(s);
    local.style.borderColor = s === 'local' ? '#9cf' : '#456';
    shared.style.borderColor = s === 'shared' ? '#9cf' : '#456';
  }
  set('local');
  row.append(local, shared);
  return row;
}

function cap(s: string): string { return s.charAt(0).toUpperCase() + s.slice(1); }
